import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useCancelRunMutation } from "@/services/(company)/payrollRunsApi";

interface IProps {
  runId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancelled: () => void;
}

/**
 * Cancel an approved run that can no longer be deleted (B8). The API existed;
 * nothing called it. A reason is required and kept with the run.
 */
export const CancelRunDialog = ({
  runId,
  open,
  onOpenChange,
  onCancelled,
}: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const [reason, setReason] = useState("");
  const [cancelRun, { isLoading }] = useCancelRunMutation();

  const submit = async () => {
    try {
      await cancelRun({ id: runId, reason: reason.trim() }).unwrap();
      toast.success(t("company:payrolls.run_cancelled"));
      setReason("");
      onOpenChange(false);
      onCancelled();
    } catch (err: unknown) {
      const msg = (err as { data?: { message?: unknown } })?.data?.message;
      toast.error(
        Array.isArray(msg)
          ? msg.join(", ")
          : String(msg ?? t("company:payrolls.action_failed")),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("company:payrolls.cancel_run_title")}</DialogTitle>
          <DialogDescription>
            {t("company:payrolls.cancel_run_desc")}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("company:payrolls.cancel_run_reason_placeholder")}
          aria-label={t("company:payrolls.cancel_run_reason_placeholder")}
          rows={3}
        />
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("common:buttons.back")}
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={reason.trim().length < 3 || isLoading}
            onClick={submit}
          >
            {t("company:payrolls.cancel_run")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
